import { isPermissionDeniedError } from './authSession'

/**
 * Checks if a string contains raw HTML markup (e.g. from IIS, Nginx, Azure 502/504 pages)
 */
function isHtmlString(str) {
  if (typeof str !== 'string') return false
  const trimmed = str.trim()
  return (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<html') ||
    (trimmed.startsWith('<') && trimmed.includes('</'))
  )
}

/**
 * Checks if a string exposes internal database, SQL Server, ODBC, or Python traceback internals.
 * Prevents Information Disclosure security vulnerabilities and displays clean user-friendly errors.
 */
function isDatabaseOrDriverLeak(message) {
  if (typeof message !== 'string') return false
  const lower = message.toLowerCase()
  return (
    lower.includes('42s02') ||
    lower.includes('odbc driver') ||
    lower.includes('sqlexecdirect') ||
    lower.includes('invalid object name') ||
    lower.includes('statement(s) could not be prepared') ||
    lower.includes('pyodbc') ||
    lower.includes('sql server') ||
    lower.includes('traceback (most recent call') ||
    lower.includes('syntax error') ||
    lower.includes('foreign key constraint') ||
    lower.includes('deadlock victim') ||
    lower.includes('cannot insert duplicate key') ||
    lower.includes('sqlstate') ||
    lower.includes('sqlite') ||
    lower.includes('psycopg2') ||
    lower.includes('sqlalchemy')
  )
}

function stringifyUnknownError(value) {
  if (typeof value === 'string') {
    if (isHtmlString(value)) return ''
    return value
  }

  if (value && typeof value === 'object') {
    if (typeof value.message === 'string' && !isHtmlString(value.message)) {
      return value.message
    }
    if (typeof value.detail === 'string' && !isHtmlString(value.detail)) {
      return value.detail
    }
    if (typeof value.code === 'string') {
      return `Error: ${value.code}`
    }
  }

  return ''
}

function extractDetailMessage(detail) {
  if (!detail) return ''
  if (typeof detail === 'string') {
    if (isHtmlString(detail)) return ''
    if (isDatabaseOrDriverLeak(detail)) {
      return 'A server or database error occurred. Please try again or contact support.'
    }
    return detail
  }

  if (Array.isArray(detail)) {
    // Handle validation errors from FastAPI (HTTP 422 Unprocessable Entity)
    const messages = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg || item?.message || ''))
      .filter(Boolean)
    if (messages.length > 0) {
      return messages.join('. ')
    }
  }

  if (typeof detail === 'object') {
    const rawMessage = stringifyUnknownError(detail.message) || stringifyUnknownError(detail.detail)
    const code = stringifyUnknownError(detail.code)
    const requestId = stringifyUnknownError(detail.requestId)

    // Security & User Experience: Suppress raw database/internal exceptions
    if (isDatabaseOrDriverLeak(rawMessage) || (code && code.toUpperCase() === 'HTTP_500')) {
      return requestId
        ? `A server error occurred. Please contact support if the issue persists. (Ref: ${requestId})`
        : 'A server error occurred. Please try again later.'
    }

    // For all validation, authentication, and client messages (400, 401, 403, 404, 422, etc.),
    // return the clean human-readable message directly without technical Ref IDs
    if (rawMessage) {
      return rawMessage
    }

    if (code && requestId) return `${code} (Ref: ${requestId})`
    if (code) return code
  }
  return ''

}

export function extractApiErrorMessage(error, fallbackMessage = 'An unexpected error occurred. Please try again.') {
  if (isPermissionDeniedError(error)) {
    return 'You do not have permission to perform this action.'
  }

  const status = Number(error?.response?.status || 0)

  // 1. Reverse Proxy / Gateway Errors (IIS ARR 502 Bad Gateway, 503, 504)
  if (status === 502 || status === 504) {
    return 'Server is temporarily unavailable. Please verify backend services and try again.'
  }
  if (status === 503) {
    return 'Service is temporarily unavailable due to server maintenance. Please try again shortly.'
  }

  // 2. Network connectivity / timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'The request timed out. Please check your connection and try again.'
  }
  if (error?.message === 'Network Error' || (!error?.response && error?.request)) {
    return 'Unable to reach the server. Please check your internet connection.'
  }

  const responseData = error?.response?.data

  // 3. Drop any raw HTML error document
  if (isHtmlString(responseData)) {
    return fallbackMessage
  }

  const direct =
    extractDetailMessage(responseData?.detail) ||
    stringifyUnknownError(responseData) ||
    stringifyUnknownError(error?.message)

  // 4. Sanitize against any leaked database or SQL error strings
  if (direct && isDatabaseOrDriverLeak(direct)) {
    return 'A server or database error occurred. Please try again or contact support.'
  }

  return direct || fallbackMessage
}

